import { authFetch } from "utils/authFetch";
import type {
  DetectedLayoutSummary,
  PlanWall,
  WallKind,
  WallPageRef,
  WallPoint,
  WallReview,
} from "./types";

const API_URL =
  process.env.NODE_ENV === "production"
    ? ""
    : (process.env.REACT_APP_BACKEND_URL ?? "http://localhost:5050");

// The plan's own layout: the same walls execution mode shows, corrected in one place.
const ENDPOINT = `${API_URL}/api/quantity-surveyor-floor-plan-layouts`;

type ServerKind = "wall" | "door";

interface ServerWallReview {
  items: Array<{
    id: number;
    kind: ServerKind;
    points: WallPoint[];
    origin: PlanWall["origin"];
  }>;
  summary: DetectedLayoutSummary | null;
}

const serverKindOf = (kind: WallKind): ServerKind =>
  kind === "wall" ? "wall" : "door";

const toReview = (body: ServerWallReview): WallReview => ({
  walls: body.items.map((item) => ({
    id: `${item.kind}-${item.id}`,
    kind: item.kind === "wall" ? "wall" : "opening",
    points: item.points,
    origin: item.origin,
  })),
  summary: body.summary,
});

const serverRefOf = (wallId: string): { kind: ServerKind; id: number } | null => {
  const [kind, id] = wallId.split("-");
  if ((kind !== "wall" && kind !== "door") || !Number.isInteger(Number(id))) {
    return null;
  }
  return { kind, id: Number(id) };
};

const readReview = async (response: Response): Promise<WallReview | null> => {
  if (!response.ok) return null;
  const body = (await response.json()) as ServerWallReview;
  return Array.isArray(body?.items) ? toReview(body) : null;
};

const post = async (
  path: string,
  body: Record<string, unknown>,
): Promise<WallReview | null> => {
  try {
    const response = await authFetch(`${ENDPOINT}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await readReview(response);
  } catch {
    return null;
  }
};

/** Null when it could not be read; the caller decides what an unreadable page means. */
export const fetchWallReview = async ({
  floorPlanId,
  pageIndex,
}: WallPageRef): Promise<WallReview | null> => {
  try {
    const response = await authFetch(
      `${ENDPOINT}/wall-review?floorPlanId=${floorPlanId}&pageIndex=${pageIndex}`,
    );
    return await readReview(response);
  } catch {
    return null;
  }
};

export const drawLayoutWall = (
  page: WallPageRef,
  kind: WallKind,
  points: WallPoint[],
): Promise<WallReview | null> =>
  post("walls/draw", { ...page, kind: serverKindOf(kind), points });

export const removeLayoutWall = (
  page: WallPageRef,
  wallId: string,
): Promise<WallReview | null> => {
  const ref = serverRefOf(wallId);
  return ref ? post("walls/remove", { ...page, ...ref }) : Promise.resolve(null);
};

export const convertLayoutWall = (
  page: WallPageRef,
  wallId: string,
): Promise<WallReview | null> => {
  const ref = serverRefOf(wallId);
  return ref ? post("walls/convert", { ...page, ...ref }) : Promise.resolve(null);
};

export const resetLayoutWalls = (page: WallPageRef): Promise<WallReview | null> =>
  post("walls/reset", { ...page });
